import {
    SYSTEM_TYPE_EXISTING,
    SYSTEM_TYPE_NEW,
} from '../../../Constants/gen1Constants';
import { includePurchasedRepeatersApplication } from '../gen1ApplicationType';
import {
    hasRepeaterLineInput,
    isSearchAddRepeaterLine,
} from './gen1RepeaterLineHelpers';
import { isPurchasedOnlyFlag } from './gen1MasterItemHelpers';

export const ADDITIONAL_NOTES_MAX_LENGTH = 2000;

/** Contact/Address 等、短文フィールド共通の最大文字数 */
export const MAX_LENGTH = 100;

/** 91800 / 91810 両方 ≤0 — 上部 Message のみ（Add operation ブロックに赤枠を付けない） */
export const ADD_REPEATER_OPERATIONS_BOTH_ZERO_MSG =
    'Please enter 1 or more for either KSGPG91800 or KSGPG91810.';

/** 数量フィールド共通 — 0 未満を拒否するメッセージ */
export const NON_NEGATIVE_NUMBER_MSG = 'Please enter a number 0 or greater.';

/** @typedef {'draft' | 'ordered'} Gen1ValidationMode */

export const GEN1_VALIDATION_MODE = {
    DRAFT: 'draft',
    ORDERED: 'ordered',
};

/** Simple email format check */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DIGITS_ONLY = /^\d+$/;

function trim(s) {
    return (s ?? '').toString().trim();
}

function isOrderedMode(mode) {
    return mode === GEN1_VALIDATION_MODE.ORDERED;
}

function calculateUsbKeyTotal(item) {
    const g1 = Number(item?.Grade1UnlimitedAccessUsbKeyQty) || 0;
    const g2 = Number(item?.Grade2LimitedAccessUsbKeyQty) || 0;
    const smWith = Number(item?.SystemManagerSoftwareWithGrade1KeyQty) || 0;
    const sm = Number(item?.SystemManagerSoftwareQty) || 0;
    return g1 + g2 + smWith + sm;
}

/**
 * 編集中フォームが Add Key のみ申請（#418）に該当するかを判定する。
 * 申請 Save 時の Repeater 操作必須チェックで使用する。
 * Existing System で Repeater 操作（Add/Multi-site/Exchange/Remove）が全て 0、
 * かつ Add Key チェック ON で USB Key 合計 1 以上の場合に true を返す。
 */
function isUsbKeyOnlyApplication(
    order,
    item,
    repeaterQty,
    multiSiteQty,
    exchangeQty,
    removeQty
) {
    return (
        order.SystemType === SYSTEM_TYPE_EXISTING &&
        repeaterQty === 0 &&
        multiSiteQty === 0 &&
        exchangeQty === 0 &&
        removeQty === 0 &&
        item.isNeededUsbKeyAndSoftware &&
        calculateUsbKeyTotal(item) >= 1
    );
}

/**
 * Gen1 Application フォームのバリデーション。
 * @param {Object} state - コンポーネントの state
 * @param {{ mode?: Gen1ValidationMode }} [options]
 * @returns {Object} フィールド名をキー、エラーメッセージを値とする errors オブジェクト
 */
export function validateGen1Application(state, options = {}) {
    const mode = options.mode ?? GEN1_VALIDATION_MODE.DRAFT;
    const errors = {};
    const order = state.gen1Order ?? {};
    const repeaterLines = order.Gen1TrunkingOrderRepeaterSerials ?? [];
    const manufactureRepMaster = state.manufactureRepMaster ?? [];
    const modelMasterForGen1 = state.modelMasterForGen1 ?? [];
    const isPurchasedRepeaterApplication =
        includePurchasedRepeatersApplication(order);
    // Purchased申請は全Model可、新規購入のみ申請はIsPurchasedOnlyを除外する。
    const availableModelMaster = modelMasterForGen1.filter(
        (x) =>
            isPurchasedRepeaterApplication ||
            !isPurchasedOnlyFlag(x?.IsPurchasedOnly)
    );
    const validManufactureRepSet = new Set(
        manufactureRepMaster.map((x) => trim(x?.ManufactureRep ?? ''))
    );
    const validModelSet = new Set(
        availableModelMaster.map((x) => trim(x?.ModelName ?? ''))
    );

    validateCommonFields(state, order, errors, {
        validManufactureRepSet,
    });

    validateApplicationTypeLineMixing(order, repeaterLines, errors);

    if (isPurchasedRepeaterApplication) {
        validatePurchasedRepeaterFields(state, order, repeaterLines, errors, {
            mode,
            validModelSet,
        });
    } else {
        validateNewPurchaseRepeaterFields(order, repeaterLines, errors, {
            mode,
            validModelSet,
        });
    }

    validateUsbKeyFields(order, errors);

    return errors;
}

/** Customer Information 等 — Draft / Ordered 共通 */
function validateCommonFields(
    state,
    order,
    errors,
    { validManufactureRepSet }
) {
    const contactName = trim(order.ContactName);
    if (contactName.length === 0) {
        errors.contactName = 'Please enter the contact name.';
    } else if (contactName.length > MAX_LENGTH) {
        errors.contactName = 'Must be 100 characters or less.';
    }

    const contactEmail = trim(order.ContactEmail);
    if (contactEmail.length === 0) {
        errors.contactEmail = 'Please enter the email address.';
    } else {
        if (contactEmail.length > MAX_LENGTH) {
            errors.contactEmail = 'Must be 100 characters or less.';
        } else if (!EMAIL_REGEX.test(contactEmail)) {
            errors.contactEmail = 'Please enter a valid email address.';
        }
    }

    const address1 = trim(order.AddressLineOne);
    if (address1.length === 0) {
        errors.addressLineOne = 'Please enter the address.';
    } else if (address1.length > MAX_LENGTH) {
        errors.addressLineOne = 'Must be 100 characters or less.';
    }

    const address2 = trim(order.AddressLineTwo);
    if (address2.length > MAX_LENGTH) {
        errors.addressLineTwo = 'Must be 100 characters or less.';
    }

    const address3 = trim(order.AddressLineThree);
    if (address3.length > MAX_LENGTH) {
        errors.addressLineThree = 'Must be 100 characters or less.';
    }

    const city = trim(order.City);
    if (city.length === 0) {
        errors.city = 'Please enter the city.';
    } else if (city.length > MAX_LENGTH) {
        errors.city = 'Must be 100 characters or less.';
    }

    const stateVal = trim(order.State);
    if (stateVal.length === 0) {
        errors.state = 'Please enter a state.';
    } else if (stateVal.length > MAX_LENGTH) {
        errors.state = 'Must be 100 characters or less.';
    }

    const zip = trim(order.Zip);
    if (zip.length === 0) {
        errors.zip = 'Please enter the zip/postal code.';
    } else if (zip.length > MAX_LENGTH) {
        errors.zip = 'Must be 100 characters or less.';
    }

    const country = trim(order.Country);
    if (country.length === 0) {
        errors.country = 'Please enter the country.';
    } else if (country.length > MAX_LENGTH) {
        errors.country = 'Must be 100 characters or less.';
    }

    const telephone = trim(order.Telephone);
    if (telephone.length === 0) {
        errors.telephone = 'Please enter the telephone number.';
    } else if (telephone.length > MAX_LENGTH) {
        errors.telephone = 'Must be 100 characters or less.';
    }

    const manufactureRep = trim(order.ManufactureRep ?? '');
    if (manufactureRep.length === 0) {
        errors.manufactureRep = 'Please select a manufacture rep.';
    } else if (
        validManufactureRepSet.size > 0 &&
        !validManufactureRepSet.has(manufactureRep)
    ) {
        errors.manufactureRep =
            'The selected manufacture rep is not valid. Please select another manufacture rep.';
    }

    const systemType = trim(order.SystemType ?? '');
    if (systemType.length === 0) {
        errors.systemType = 'Please select a system type.';
    }

    const dealerOrderNumber = trim(order.DealerOrderNumber ?? '');
    if (dealerOrderNumber.length > MAX_LENGTH) {
        errors.dealerOrderNumber = 'Must be 100 characters or less.';
    }

    if (order.SystemType === SYSTEM_TYPE_EXISTING) {
        const systemId = trim(order.SystemId ?? '');
        if (systemId.length === 0) {
            errors.systemId = 'Please enter the System ID.';
        } else if (systemId.length > MAX_LENGTH) {
            errors.systemId =
                'Please enter the System ID within 100 characters.';
        } else if (!DIGITS_ONLY.test(systemId)) {
            errors.systemId = 'System ID must contain numbers only.';
        }
    }

    const additionalNotes = trim(order.AdditionalNotes ?? '');
    if (additionalNotes.length > ADDITIONAL_NOTES_MAX_LENGTH) {
        errors.additionalNotes =
            'Please enter the additional notes within 2000 characters.';
    }
}

/** FR-03-8: 申請種別と明細の整合 */
function validateApplicationTypeLineMixing(order, repeaterLines, errors) {
    const item = order.Gen1TrunkingOrderItem ?? {};
    const exchangeQty = Number(item.ExchangeRepeaterPairQty) || 0;
    const removeQty = Number(item.RemoveRepeaterQty) || 0;

    if (includePurchasedRepeatersApplication(order)) {
        if (order.SystemType === SYSTEM_TYPE_NEW) {
            if (exchangeQty > 0 || removeQty > 0) {
                errors.repeaterApplicationTypeMix =
                    'Exchange and remove operations are only allowed for existing systems.';
            }
            // 購入済み申請 + 新規 System では Exchange/Remove 明細を許可しない。
            // ExchangePairId あり = Exchange ペア行、OperationType=REMOVE = Remove 行。
            for (const row of repeaterLines) {
                if (!row) continue;
                const op = String(row.OperationType ?? '').toUpperCase();
                if (
                    row.ExchangePairId != null ||
                    (op === 'REMOVE' && row.ExchangePairId == null)
                ) {
                    errors.repeaterApplicationTypeMix =
                        'Exchange and remove operations are only allowed for existing systems.';
                    break;
                }
            }
        }

        return;
    }

    if (exchangeQty > 0 || removeQty > 0) {
        errors.repeaterApplicationTypeMix =
            'Exchange and remove operations are not allowed on new-purchase-only applications.';
    }

    for (const row of repeaterLines) {
        if (!row) continue;
        const op = String(row.OperationType ?? '').toUpperCase();
        if (isSearchAddRepeaterLine(row)) {
            errors.repeaterApplicationTypeMix =
                'Sales order search lines are not allowed on new-purchase-only applications.';
            break;
        }
        if (row.ExchangePairId != null) {
            errors.repeaterApplicationTypeMix =
                'Exchange operations are not allowed on new-purchase-only applications.';
            break;
        }
        if (op === 'REMOVE' && row.ExchangePairId == null) {
            errors.repeaterApplicationTypeMix =
                'Remove operations are not allowed on new-purchase-only applications.';
            break;
        }
    }
}

/** 新規購入のみ — FR-03-6 */
function validateNewPurchaseRepeaterFields(
    order,
    repeaterLines,
    errors,
    { mode, validModelSet }
) {
    const item = order.Gen1TrunkingOrderItem ?? {};
    const repeaterQty = Number(item.RepeaterQty) || 0;
    const multiSiteQty = Number(item.MultiSiteNetworkingQty) || 0;
    const strict = isOrderedMode(mode);

    if (repeaterQty < 0) {
        errors.repeaterQty = NON_NEGATIVE_NUMBER_MSG;
    }
    if (multiSiteQty < 0) {
        errors.multiSiteNetworkingQty = NON_NEGATIVE_NUMBER_MSG;
    }

    if (repeaterQty >= 0 && multiSiteQty >= 0 && strict) {
        if (repeaterQty <= 0 && multiSiteQty <= 0) {
            errors.addRepeaterOperations =
                ADD_REPEATER_OPERATIONS_BOTH_ZERO_MSG;
        } else if (multiSiteQty > 0 && repeaterQty <= 0) {
            errors.multiSiteNetworkingQty =
                'Please enter 1 or more for KSGPG91800 (Repeater Trunking Feature) when ordering KSGPG91810 (Multi-site Networking).';
        } else {
            // 数量チェック通過後のみ: 入力のある明細行が 1 行もなければエラー
            const detailLines = repeaterLines.filter(hasRepeaterLineInput);
            if (detailLines.length < 1) {
                errors.addRepeaterOperations =
                    'Please enter at least one repeater detail line with model.';
            }
        }
    }

    // 上記の有無チェックのあと、各行の Model / Market 等を詳細検証（Draft は入力済みのみ）
    validateRepeaterDetailRows(repeaterLines, errors, {
        strict,
        requireSerial: false,
        validModelSet,
    });
}

/** 購入済みのみ — FR-03-7 */
function validatePurchasedRepeaterFields(
    state,
    order,
    repeaterLines,
    errors,
    { mode, validModelSet }
) {
    const item = order.Gen1TrunkingOrderItem ?? {};
    const repeaterQty = Number(item.RepeaterQty) || 0;
    const multiSiteQty = Number(item.MultiSiteNetworkingQty) || 0;
    const exchangeQty = Number(item.ExchangeRepeaterPairQty) || 0;
    const removeQty = Number(item.RemoveRepeaterQty) || 0;
    const strict = isOrderedMode(mode);

    if (repeaterQty < 0) {
        errors.repeaterQty = NON_NEGATIVE_NUMBER_MSG;
    }
    if (multiSiteQty < 0) {
        errors.multiSiteNetworkingQty = NON_NEGATIVE_NUMBER_MSG;
    }

    if (order.SystemType === SYSTEM_TYPE_EXISTING) {
        if (exchangeQty < 0) {
            errors.exchangeRepeaterQuantity = NON_NEGATIVE_NUMBER_MSG;
        } else if (exchangeQty > 0) {
            validateOtherSystemId(state, errors);
        }
        if (removeQty < 0) {
            errors.removeRepeaterQuantity = NON_NEGATIVE_NUMBER_MSG;
        }

        // ADD の有無は Item（RepeaterQty / MultiSiteNetworkingQty）のみ。addRepeater は UI 専用。
        if (strict && repeaterQty >= 0 && multiSiteQty >= 0) {
            const hasRepeaterOp =
                repeaterQty > 0 ||
                multiSiteQty > 0 ||
                exchangeQty > 0 ||
                removeQty > 0;
            const isUsbKeyOnlyOrder = isUsbKeyOnlyApplication(
                order,
                item,
                repeaterQty,
                multiSiteQty,
                exchangeQty,
                removeQty
            );
            if (!hasRepeaterOp && !isUsbKeyOnlyOrder) {
                errors.repeaterOperations =
                    'Please select at least one repeater operation.';
            } else if (repeaterQty > 0) {
                // 明細スロットは RepeaterQty のみで生成（MultiSite のみでは行を作らない）
                const addLines = repeaterLines.filter(
                    (l) =>
                        String(l?.OperationType ?? '').toUpperCase() ===
                        'ADD' &&
                        l?.ExchangePairId == null &&
                        hasRepeaterLineInput(l)
                );
                if (addLines.length < 1) {
                    errors.addRepeaterOperations =
                        'Please enter repeater details for add repeater operations.';
                }
            }
        }
    } else if (strict && repeaterQty >= 0 && multiSiteQty >= 0) {
        if (repeaterQty <= 0 && multiSiteQty <= 0) {
            errors.addRepeaterOperations =
                ADD_REPEATER_OPERATIONS_BOTH_ZERO_MSG;
        } else if (multiSiteQty > 0 && repeaterQty <= 0) {
            errors.multiSiteNetworkingQty =
                'Please enter 1 or more for KSGPG91800 (Repeater Trunking Feature) when ordering KSGPG91810 (Multi-site Networking).';
        }
    }

    validateRepeaterDetailRows(repeaterLines, errors, {
        strict,
        requireSerial: includePurchasedRepeatersApplication(order),
        validModelSet,
    });
}

function validateOtherSystemId(state, errors) {
    const otherSystemId = trim(state.otherSystemId ?? '');
    if (otherSystemId.length === 0) {
        errors.otherSystemId = 'Please enter the other System ID.';
    } else if (otherSystemId.length > MAX_LENGTH) {
        errors.otherSystemId =
            'Other System ID must be 100 characters or less.';
    } else if (!DIGITS_ONLY.test(otherSystemId)) {
        errors.otherSystemId = 'Other System ID must contain numbers only.';
    }
}

/**
 * Repeater Details 行の Model / Serial。
 * Draft: 未入力は許可、入力済みのみ検証
 * Ordered: 厳格（Search 行は Model のみスキップ）
 */
function validateRepeaterDetailRows(
    repeaterLines,
    errors,
    { strict, requireSerial, validModelSet }
) {
    for (const row of repeaterLines) {
        if (!row) continue;

        const rowKey = row.LineSequenceNumber ?? 'unknown';
        const lineOrderNumber = trim(row.OrderNumber ?? '');
        if (lineOrderNumber.length > MAX_LENGTH) {
            errors[`repeater_${rowKey}_orderNumber`] =
                'Sales order number (SO#) must be 100 characters or less.';
        }
        const hasOrderNumber = lineOrderNumber.length > 0;
        if (!strict && !hasRepeaterLineInput(row)) {
            continue;
        }

        if (!hasOrderNumber) {
            const model = trim(row.BaseModel ?? '');
            if (model.length === 0) {
                if (strict) {
                    errors[`repeater_${rowKey}_model`] =
                        'Please select a model for repeater details.';
                }
            } else if (validModelSet.size > 0 && !validModelSet.has(model)) {
                errors[`repeater_${rowKey}_model`] =
                    'The selected model is not available. Please revise your model selection.';
            }
        }

        const serialNumber = trim(row.SerialNumber ?? '');
        if (requireSerial) {
            if (serialNumber.length === 0) {
                if (strict) {
                    errors[`repeater_${rowKey}_serialNumber`] =
                        'Please enter a serial number for repeater details.';
                }
            } else if (serialNumber.length > MAX_LENGTH) {
                errors[`repeater_${rowKey}_serialNumber`] =
                    'Serial number must be 100 characters or less.';
            }
        }
    }
}

/** USB Keys（Add Key / 4 SKU）。Repeater 明細とは別。Draft / Ordered で同じ検証。 */
function validateUsbKeyFields(order, errors) {
    const item = order.Gen1TrunkingOrderItem ?? {};

    if (item.isNeededUsbKeyAndSoftware) {
        const g1 = Number(item.Grade1UnlimitedAccessUsbKeyQty) || 0;
        const g2 = Number(item.Grade2LimitedAccessUsbKeyQty) || 0;
        const smWith = Number(item.SystemManagerSoftwareWithGrade1KeyQty) || 0;
        const smQty = Number(item.SystemManagerSoftwareQty) || 0;
        if (g1 < 1 && g2 < 1 && smWith < 1 && smQty < 1) {
            errors.usbKeyAtLeastOne =
                'Please enter 1 or more for at least one of the following: KPGKEY01, KPGKEY02, KPGKEYSM, or KPG-110SMK.';
        }
        if (g1 > 10) {
            errors.usbKey_Grade1UnlimitedAccessUsbKeyQty =
                'Please enter 10 or less for KPGKEY01 quantity.';
        }
        if (g2 > 10) {
            errors.usbKey_Grade2LimitedAccessUsbKeyQty =
                'Please enter 10 or less for KPGKEY02 quantity.';
        }
        if (smWith > 10) {
            errors.usbKey_SystemManagerSoftwareWithGrade1KeyQty =
                'Please enter 10 or less for KPGKEYSM quantity.';
        }
        if (smQty > 20) {
            errors.usbKey_SystemManagerSoftwareQty =
                'Please enter 20 or less for KPG-110SMK quantity.';
        }
    }

    if (order.SystemType === SYSTEM_TYPE_NEW) {
        if (!item.isNeededUsbKeyAndSoftware) {
            errors.usbKeyKpgkey01OrSm =
                'Please check the box and enter 1 or more for either KPGKEY01 or KPGKEYSM.';
        } else {
            const g1 = Number(item.Grade1UnlimitedAccessUsbKeyQty) || 0;
            const smWith =
                Number(item.SystemManagerSoftwareWithGrade1KeyQty) || 0;
            if (g1 < 1 && smWith < 1) {
                errors.usbKeyKpgkey01OrSm =
                    'Please enter 1 or more for either KPGKEY01 or KPGKEYSM.';
            }
        }
    }
}
